import React from 'react'
import { CheckCircle, CaretRight } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

export interface StepIndicatorStep {
  /** Identity of the step. Matches whatever the owner stores in state — a string union or a 1-based number. */
  key: string | number
  label: string
}

export interface StepIndicatorProps {
  steps: StepIndicatorStep[]
  /** The active step's `key`. A number past the last key renders every step as done. */
  current: string | number
  /**
   * When supplied, **completed** steps become buttons and clicking one calls
   * back with its key. Active and upcoming steps stay inert — a wizard may
   * never be skipped forward.
   */
  onStepChange?: (key: string | number) => void
  /** Show a step number on active/upcoming pills instead of nothing. */
  showNumbers?: boolean
  className?: string
}

const PILL_BASE =
  'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border transition-colors duration-150'

const PILL_ACTIVE =
  'bg-[#5e6ad2]/10 text-[#5e6ad2] border-[#5e6ad2]/25 dark:bg-[#5e6ad2]/[0.14] dark:text-[#a5b4fc] dark:border-[#5e6ad2]/30'

const PILL_DONE =
  'text-emerald-700 border-emerald-500/25 bg-emerald-500/[0.06] dark:text-emerald-400 dark:bg-emerald-500/[0.08] dark:border-emerald-500/25'

const PILL_UPCOMING = 'text-muted-foreground border-transparent dark:text-[#62666d]'

const PILL_STYLE = { fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' } as const

/**
 * The project's one wizard progress indicator (§30).
 *
 * Pills, not numbered circles-and-rails: done = emerald with a filled
 * `CheckCircle`, active = indigo soft tint, upcoming = subtle. Separated by a
 * phosphor `CaretRight` weight="bold" — never a `>` glyph (§30).
 *
 * Replaces four hand-rolled copies (ImportContactsModal, AddSenderEmailModal's
 * `StepBreadcrumb`, EnrichmentCatalogModal's inline stepper, and
 * LaunchCampaignModal's bare-text variant).
 */
export const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  current,
  onStepChange,
  showNumbers = false,
  className,
}) => {
  const found = steps.findIndex((s) => s.key === current)
  // A numeric `current` beyond the last step means the flow has run past its
  // declared steps (a progress/result view). Everything reads as done rather
  // than the whole strip silently collapsing to "upcoming".
  const currentIdx =
    found !== -1
      ? found
      : typeof current === 'number' && current > Number(steps[steps.length - 1]?.key ?? 0)
        ? steps.length
        : 0

  return (
    <div
      className={cn('flex items-center gap-1', className)}
      role="group"
      aria-label="Progress"
    >
      {steps.map((s, i) => {
        const isActive = i === currentIdx
        const isDone = i < currentIdx
        const clickable = isDone && !!onStepChange

        const tone = cn(
          PILL_BASE,
          isActive && PILL_ACTIVE,
          isDone && PILL_DONE,
          !isActive && !isDone && PILL_UPCOMING,
          clickable && 'cursor-pointer hover:bg-emerald-500/[0.10]'
        )

        const body = (
          <>
            {isDone ? (
              <CheckCircle className="h-3 w-3" weight="fill" />
            ) : showNumbers ? (
              <span className="tabular-nums">{i + 1}</span>
            ) : null}
            {s.label}
          </>
        )

        return (
          <React.Fragment key={s.key}>
            {clickable ? (
              <button
                type="button"
                onClick={() => onStepChange!(s.key)}
                className={tone}
                style={PILL_STYLE}
              >
                {body}
              </button>
            ) : (
              <span
                className={tone}
                style={PILL_STYLE}
                aria-current={isActive ? 'step' : undefined}
              >
                {body}
              </span>
            )}
            {i < steps.length - 1 && (
              <CaretRight
                className={cn(
                  'h-3 w-3',
                  i < currentIdx
                    ? 'text-emerald-500/60 dark:text-emerald-400/60'
                    : 'text-muted-foreground/50 dark:text-[#3e3e44]'
                )}
                weight="bold"
                aria-hidden="true"
              />
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default StepIndicator
